import React from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  DollarSign, 
  Home, 
  BarChart3, 
  Shield, 
  Zap, 
  CheckCircle, 
  ArrowRight,
  Calculator,
  Building,
  PiggyBank,
  Target,
  Lock,
  Globe,
  Clock
} from 'lucide-react';
import StructuredData from '../components/StructuredData';
import CanonicalURL from '../components/CanonicalURL';

const LandingPage = () => {
  return (
    <>
      <StructuredData type="application" page="landing" />
      <CanonicalURL />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
            <div className="text-center">
              <h1 className="text-5xl sm:text-6xl font-extrabold mb-6">
                WealthCore
              </h1>
              <p className="text-2xl sm:text-3xl font-semibold mb-4">
                Comprehensive Wealth Management Platform
              </p>
              <p className="text-xl sm:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
                The only all-in-one financial software combining tax optimization, stock analysis, real estate portfolio management, and retirement planning in one integrated dashboard.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors shadow-lg"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-8 py-4 bg-blue-700 text-white font-bold rounded-lg hover:bg-blue-800 transition-colors border-2 border-white"
                >
                  Sign In
                </Link>
                <Link
                  to="/examples"
                  className="inline-flex items-center justify-center px-6 py-3 text-blue-100 hover:text-white font-semibold rounded-lg hover:bg-blue-800/50 transition-colors border border-blue-400/50"
                >
                  See Page Examples
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Unique Value Proposition */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why WealthCore is Different
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Unlike other platforms that focus on one area, WealthCore integrates everything you need for complete financial management.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-xl border-2 border-blue-200">
                <div className="flex items-center mb-4">
                  <Zap className="h-8 w-8 text-blue-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">All-in-One Platform</h3>
                </div>
                <p className="text-gray-700 text-lg">
                  Manage taxes, stocks, real estate, and retirement planning from one integrated dashboard. No need to switch between multiple apps.
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl border-2 border-green-200">
                <div className="flex items-center mb-4">
                  <Clock className="h-8 w-8 text-green-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">Year-Round Tax Planning</h3>
                </div>
                <p className="text-gray-700 text-lg">
                  Not just filing season. Continuous tax optimization throughout the year with real-time impact calculations and bracket management.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl border-2 border-purple-200">
                <div className="flex items-center mb-4">
                  <Target className="h-8 w-8 text-purple-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">Unique Stock Patterns</h3>
                </div>
                <p className="text-gray-700 text-lg">
                  Proprietary algorithms including RSI Extended Detection and Scene of Crime pattern recognition - features you won't find elsewhere.
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-xl border-2 border-orange-200">
                <div className="flex items-center mb-4">
                  <Shield className="h-8 w-8 text-orange-600 mr-3" />
                  <h3 className="text-2xl font-bold text-gray-900">Secure & Private</h3>
                </div>
                <p className="text-gray-700 text-lg">
                  Your financial data is encrypted and stored securely. We never share your information with third parties. Complete privacy and data protection.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Features */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Complete Financial Management
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to manage your entire financial life
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Tax Optimization */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <div className="flex items-center mb-4">
                  <Calculator className="h-6 w-6 text-blue-600 mr-2" />
                  <h3 className="text-xl font-bold text-gray-900">Tax Optimization</h3>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Year-round tax planning</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Real-time tax impact calculations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Roth IRA withdrawal optimization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tax bracket management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Expense tracking & categorization</span>
                  </li>
                </ul>
              </div>

              {/* Stock Analysis */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <div className="flex items-center mb-4">
                  <BarChart3 className="h-6 w-6 text-purple-600 mr-2" />
                  <h3 className="text-xl font-bold text-gray-900">Stock Analysis</h3>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>RSI Extended Detection (unique)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Scene of Crime pattern (proprietary)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>S&P 500 comprehensive screening (5000+ stocks)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Real-time market sentiment analysis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Advanced technical indicators</span>
                  </li>
                </ul>
              </div>

              {/* Real Estate */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <div className="flex items-center mb-4">
                  <Building className="h-6 w-6 text-green-600 mr-2" />
                  <h3 className="text-xl font-bold text-gray-900">Real Estate</h3>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Multiple property tracking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Rental income & expense tracking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tax deduction optimization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Property expense categorization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Portfolio performance tracking</span>
                  </li>
                </ul>
              </div>

              {/* Retirement Planning */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <div className="flex items-center mb-4">
                  <PiggyBank className="h-6 w-6 text-yellow-600 mr-2" />
                  <h3 className="text-xl font-bold text-gray-900">Retirement</h3>
                </div>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Roth IRA withdrawal planning</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Penalty calculation & avoidance</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tax impact analysis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Bracket optimization strategies</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Retirement account management</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Unique Features Highlight */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Proprietary Features You Won't Find Elsewhere
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl border-2 border-blue-300">
                <div className="flex items-center mb-4">
                  <TrendingUp className="h-10 w-10 text-blue-600 mr-4" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">RSI Extended Detection</h3>
                    <p className="text-sm text-gray-600">Proprietary Algorithm</p>
                  </div>
                </div>
                <p className="text-gray-700 text-lg mb-4">
                  Identifies stocks with RSI under 30 for more than 12 consecutive trading days AND RSI under 20 for at least 6 of those days. This unique detection method finds prolonged oversold conditions that often signal strong buy opportunities.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Only available on WealthCore</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Based on 3-month RSI analysis (63-day period)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Shown in bulk stock analysis results</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl border-2 border-purple-300">
                <div className="flex items-center mb-4">
                  <Target className="h-10 w-10 text-purple-600 mr-4" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Scene of Crime Pattern</h3>
                    <p className="text-sm text-gray-600">Advanced Pattern Recognition</p>
                  </div>
                </div>
                <p className="text-gray-700 text-lg mb-4">
                  Detects stocks that broke through a major trendline (held for 1+ year, hit 6+ times) within the last 30 trading days, then returned to that trendline level. This often indicates a strong support/resistance level and potential bounce opportunity.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Proprietary trendline analysis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Multi-year trendline detection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Breakout and return pattern identification</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Getting Started Section */}
        <section className="py-16 bg-gradient-to-br from-blue-50 to-purple-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Get Started Today
              </h2>
              <p className="text-xl text-gray-600">
                All features are available to help you manage your complete financial life.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {/* Tax & Planning */}
              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-blue-200">
                <div className="text-center mb-6">
                  <Calculator className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Tax & Planning</h3>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Year-round tax planning</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Expense tracking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Income management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Roth IRA planning</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Tax forms & calculations</span>
                  </li>
                </ul>
              </div>

              {/* Stock Analysis */}
              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-purple-200">
                <div className="text-center mb-6">
                  <TrendingUp className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Stock Analysis</h3>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Individual stock analysis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Universal market screening</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">RSI Extended detection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Scene of Crime patterns</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Real-time market data</span>
                  </li>
                </ul>
              </div>

              {/* Real Estate */}
              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-green-200">
                <div className="text-center mb-6">
                  <Building className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Real Estate</h3>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Property portfolio management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Rental income tracking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Depreciation calculations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Tax optimization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Multiple property support</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Security & Trust */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Secure & Reliable
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <Lock className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Bank-Level Security</h3>
                <p className="text-gray-600">
                  Your financial data is encrypted and secure. We use industry-standard security practices to protect your information.
                </p>
              </div>
              <div className="text-center">
                <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Privacy First</h3>
                <p className="text-gray-600">
                  Your data is yours. We never sell your information. All data is stored securely and only accessible by you.
                </p>
              </div>
              <div className="text-center">
                <Globe className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Always Available</h3>
                <p className="text-gray-600">
                  Access your financial dashboard from anywhere, anytime. Cloud-based platform with 99.9% uptime.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section - matches schema for valid rich results */}
        <section className="py-16 bg-white" id="faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What is WealthCore?</h3>
                <p className="text-gray-600">WealthCore is a comprehensive wealth management platform combining tax optimization, stock analysis, real estate portfolio management, expense tracking with bank statement import, and retirement planning in one integrated dashboard.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What makes WealthCore different?</h3>
                <p className="text-gray-600">WealthCore integrates tax planning, stock analysis, real estate, and expense tracking in one place. Unique features: RSI Extended Detection, Scene of Crime pattern recognition, year-round tax planning, and USAA bank statement import for expense categorization.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How much does WealthCore cost?</h3>
                <p className="text-gray-600">Tax planning, expense tracking, real estate management, and retirement planning are included. Stock analysis is available with a free trial—try individual analysis and full S&P 500 screening.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-4">
              Ready to Take Control of Your Financial Life?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join WealthCore today and start managing your taxes, investments, real estate, and retirement planning from one powerful platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors shadow-lg"
              >
                Create Free Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-700 text-white font-bold rounded-lg hover:bg-blue-800 transition-colors border-2 border-white"
              >
                Sign In to Existing Account
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-300 py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-white font-bold text-lg mb-4">WealthCore</h3>
                <p className="text-sm">
                  Comprehensive wealth management platform for taxes, stocks, real estate, and retirement planning.
                </p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Platform</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/platform" className="hover:text-white transition-colors">Platform Overview</Link></li>
                  <li><Link to="/examples" className="hover:text-white transition-colors">Page Examples</Link></li>
                  <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                  <li><Link to="/help" className="hover:text-white transition-colors">Help & Support</Link></li>
                  <li><Link to="/legal" className="hover:text-white transition-colors">Legal</Link></li>
                  <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                  <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                  <li><Link to="/register" className="hover:text-white transition-colors">Get Started</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Features</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link></li>
                  <li><Link to="/bulk-stock-analysis" className="hover:text-white transition-colors">Stock Analysis</Link></li>
                  <li><Link to="/property-manager" className="hover:text-white transition-colors">Property Manager</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Contact</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/help#contact-form" className="hover:text-white transition-colors">support@wealthcore.app</Link></li>
                  <li><Link to="/help#contact-form" className="hover:text-white transition-colors">Contact Form</Link></li>
                </ul>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm">
              <p>&copy; {new Date().getFullYear()} WealthCore. All rights reserved.</p>
              <p className="mt-2 text-gray-500 text-xs max-w-2xl mx-auto">
                WealthCore provides tools for educational and informational purposes only. We are not a registered investment advisor, broker-dealer, CPA, or tax attorney. Nothing on this platform constitutes investment, tax, or legal advice. The Service is provided &quot;as is&quot; without warranty. You use the Service at your own risk. Consult qualified professionals before making financial decisions.
              </p>
              <p className="mt-2">
                <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
                {' · '}
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                {' · '}
                <Link to="/legal" className="hover:text-white transition-colors">Legal</Link>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;




